import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Landmark } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { AccAccount } from '../hooks/useChartOfAccounts';
import { Plus, RefreshCw, Search, FolderTree, ListFilter, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '@/shared/api/axios';

export function ChartOfAccountsPage() {
  const [accounts, setAccounts] = useState<AccAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'flat' | 'tree'>('flat');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Form state for creating account
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'asset' | 'liability' | 'equity' | 'income' | 'cogs' | 'expense'>('asset');
  const [isGroupHeader, setIsGroupHeader] = useState(false);
  const [isControlAccount, setIsControlAccount] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = viewMode === 'tree' ? '/api/accounting/accounts?asTree=true' : '/api/accounting/accounts';
      const res = await api.get(url);
      const data = res.data;
      if (data.ok || data.success) {
        const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
        setAccounts(list);
      } else {
        setError(data.error?.message || 'Failed to load chart of accounts');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Network error fetching accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [viewMode]);

  const handleSeed = async () => {
    setLoading(true);
    try {
      await api.post('/api/accounting/accounts/seed', {});
      setToastMessage('Successfully seeded default Chart of Accounts!');
      setTimeout(() => setToastMessage(null), 3000);
      await fetchAccounts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode || !newName) return;

    try {
      const res = await api.post('/api/accounting/accounts', {
        code: newCode,
        name: newName,
        type: newType,
        isGroupHeader,
        isControlAccount,
      });

      const data = res.data;
      if (data.ok || data.success) {
        setShowAddModal(false);
        setNewCode('');
        setNewName('');
        setToastMessage(`Account ${newCode} — ${newName} created successfully!`);
        setTimeout(() => setToastMessage(null), 3000);
        await fetchAccounts();
      } else {
        alert(data.error?.message || 'Failed to create account');
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const filteredAccounts = accounts.filter((a) => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase()) || a.code.includes(search);
    const matchesType = typeFilter === 'all' || a.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="">
      <AccountingSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        {toastMessage && (
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            {toastMessage}
          </div>
        )}

        <PageHeader icon={Landmark} eyebrow="Finance" title="Chart of Accounts" description="Manage 4-digit general ledger accounts, control accounts, and group headers." actions={<><div className="flex items-center gap-3">
            <div className="bg-slate-200 dark:bg-slate-800 p-1 rounded-lg flex text-xs">
              <button
                onClick={() => setViewMode('flat')}
                className={`px-3 py-1.5 rounded-md font-medium transition ${
                  viewMode === 'flat'
                    ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-slate-100'
                    : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                <ListFilter className="w-3.5 h-3.5 inline mr-1" /> Flat View
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-1.5 rounded-md font-medium transition ${
                  viewMode === 'tree'
                    ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-slate-100'
                    : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                <FolderTree className="w-3.5 h-3.5 inline mr-1" /> Tree View
              </button>
            </div>

            <button
              onClick={handleSeed}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg font-medium text-xs transition flex items-center gap-1.5 border border-slate-300 dark:border-slate-700"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Seed Default CoA
            </button>

            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-xs transition flex items-center gap-1.5 shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add Account
            </button>
          </div></>} />

        {/* Filter Toolbar */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by account code or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Account Types</option>
            <option value="asset">Assets (1000)</option>
            <option value="liability">Liabilities (2000)</option>
            <option value="equity">Equity (3000)</option>
            <option value="income">Income (4000)</option>
            <option value="cogs">COGS (5000)</option>
            <option value="expense">Expenses (6000)</option>
          </select>
        </div>

        {/* Accounts Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">Loading Chart of Accounts...</div>
          ) : error ? (
            <div className="p-8 text-center text-rose-500 flex items-center justify-center gap-2">
              <AlertCircle className="w-5 h-5" /> {error}
            </div>
          ) : (
            <table className="w-full text-left text-sm text-slate-700 dark:text-slate-300">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-900 dark:text-slate-100">
                <tr>
                  <th className="px-4 py-3 font-mono">Code</th>
                  <th className="px-4 py-3">Account Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Flags</th>
                  <th className="px-4 py-3 text-right">Posting Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredAccounts.map((acc) => (
                  <tr key={acc._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3 font-mono font-bold text-slate-900 dark:text-slate-100">{acc.code}</td>
                    <td className="px-4 py-3 font-medium">
                      <span className={acc.isGroupHeader ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}>
                        {acc.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                        {acc.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {acc.isGroupHeader && <span className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 text-[10px] font-bold px-2 py-0.5 rounded">HEADER</span>}
                        {acc.isControlAccount && <span className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 text-[10px] font-bold px-2 py-0.5 rounded">CONTROL</span>}
                        {acc.isSystem && <span className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded">SYSTEM</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {acc.allowManualPosting ? (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">Direct Posting OK</span>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500 font-normal">Subledger Only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Create New GL Account</h3>
            
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Account Code (4 Digits)</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 1090"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Account Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Petty Cash — Branch B"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Account Type</label>
                <select
                  value={newType}
                  onChange={(e: any) => setNewType(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="asset">Asset (1000)</option>
                  <option value="liability">Liability (2000)</option>
                  <option value="equity">Equity (3000)</option>
                  <option value="income">Income (4000)</option>
                  <option value="cogs">COGS (5000)</option>
                  <option value="expense">Expense (6000)</option>
                </select>
              </div>

              <div className="space-y-2 pt-2">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isGroupHeader}
                    onChange={(e) => setIsGroupHeader(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600"
                  />
                  Is Group Header (Summary Account)
                </label>

                <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isControlAccount}
                    onChange={(e) => setIsControlAccount(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600"
                  />
                  Is Control Account (Subledger Guarded)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 text-xs font-medium hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                >
                  Save Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChartOfAccountsPage;
